import { useState, useEffect } from "react";
import axios from "axios";
import QuizTaker from "./QuizTaker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ExamSection({ onExamComplete, config }) {
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [librarySubjects, setLibrarySubjects] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [gradeLevel, setGradeLevel] = useState("");
  const [error, setError] = useState("");
  const [lessonProgress, setLessonProgress] = useState({}); // Track lesson completion per subject

  const gradeLevels = [
    { id: "K-5", name: "K-5 Elementary" },
    { id: "6-8", name: "6-8 Middle School" },
    { id: "9-12", name: "9-12 High School" },
    { id: "College", name: "College" },
    { id: "Master", name: "Master's Level" },
    { id: "Ordination", name: "Ordination Program" }
  ];

  useEffect(() => {
    fetchSubjects();
    fetchLibrary();
  }, []);

  // Fetch lesson progress when grade level changes
  useEffect(() => {
    if (gradeLevel) {
      fetchAllLessonProgress();
    }
  }, [gradeLevel, librarySubjects]);

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/library/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data.subjects);
    } catch (err) {
      console.error("Error fetching subjects:", err);
    }
  };

  const fetchLibrary = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibrarySubjects(response.data.subjects || []);
    } catch (err) {
      console.error("Error fetching library:", err);
    }
  };

  // Fetch lesson progress for all subjects in the library
  const fetchAllLessonProgress = async () => {
    if (!gradeLevel || librarySubjects.length === 0) return;
    
    try {
      const token = localStorage.getItem("token");
      const progressMap = {};
      
      for (const libSubject of librarySubjects) {
        try {
          const response = await axios.get(
            `${API}/lesson/progress/${libSubject.subject_id}/${gradeLevel}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          progressMap[libSubject.subject_id] = response.data;
        } catch (err) {
          // Subject might not have curriculum for this grade level
          progressMap[libSubject.subject_id] = { 
            can_take_exam: false, 
            all_lessons_completed: false,
            total_lessons: 0,
            lessons_completed_count: 0
          };
        }
      }
      
      setLessonProgress(progressMap);
    } catch (err) {
      console.error("Error fetching lesson progress:", err);
    }
  };

  const getAvailableSubjects = () => {
    // Only show subjects that have curriculum generated
    return subjects.filter(s => 
      librarySubjects.some(ls => ls.subject_id === s.id) &&
      s.grade_levels.includes(gradeLevel)
    );
  };

  const handleStartExam = (subjectId, examType) => {
    const subject = subjects.find(s => s.id === subjectId);
    
    // Check if curriculum exists for this subject
    const hasLibrary = librarySubjects.some(ls => ls.subject_id === subjectId);
    if (!hasLibrary) {
      setError(`Please generate ${subject?.name || 'this'} curriculum first in the Library section.`);
      return;
    }

    // Check lesson completion for Chapter Exams (not for lesson quizzes)
    if (examType === "chapter") {
      const progress = lessonProgress[subjectId];
      if (progress && !progress.can_take_exam) {
        const remaining = (progress.total_lessons || 0) - (progress.lessons_completed_count || 0);
        setError(`You must complete all lessons before taking the Chapter Exam. ${remaining} lesson${remaining !== 1 ? 's' : ''} remaining. Go to Library to study!`);
        return;
      }
    }

    setSelectedExam({
      subjectId,
      subjectName: subject?.name,
      examType,
      isExam: examType === "chapter"
    });
    setError("");
  };

  const handleExamComplete = (result) => {
    onExamComplete();
    // Keep showing result for a moment then reset
    setTimeout(() => {
      setSelectedExam(null);
    }, 500);
  };

  // If taking an exam, show QuizTaker
  if (selectedExam) {
    return (
      <QuizTaker
        subjectId={selectedExam.subjectId}
        gradeLevel={gradeLevel}
        lessonNumber={selectedExam.examType === "quiz" ? 1 : null}
        isExam={selectedExam.isExam}
        onComplete={handleExamComplete}
        onCancel={() => setSelectedExam(null)}
      />
    );
  }

  // Helper to render progress indicator
  const renderProgressIndicator = (subjectId) => {
    const progress = lessonProgress[subjectId];
    if (!progress || progress.total_lessons === 0) {
      return (
        <span style={{
          background: "#4d4d1a",
          color: "#FFD700",
          padding: "3px 8px",
          borderRadius: "4px",
          fontSize: "0.8rem"
        }}>
          ⏳ Loading progress...
        </span>
      );
    }

    const completed = progress.lessons_completed_count || 0;
    const total = progress.total_lessons || 0;
    const canTakeExam = progress.can_take_exam;

    if (canTakeExam) {
      return (
        <span style={{
          background: "#1a4d1a",
          color: "#90EE90",
          padding: "3px 8px",
          borderRadius: "4px",
          fontSize: "0.8rem"
        }}>
          ✓ All {total} lessons completed - Exam Unlocked!
        </span>
      );
    } else {
      return (
        <span style={{
          background: "#4d1a1a",
          color: "#ff6b6b",
          padding: "3px 8px",
          borderRadius: "4px",
          fontSize: "0.8rem"
        }}>
          🔒 {completed}/{total} lessons completed - Complete all to unlock exam
        </span>
      );
    }
  };

  return (
    <div>
      <div className="card" data-testid="exams-section">
        <h3>📝 Exams & Assessments</h3>
        <p style={{ color: "#aaa", marginBottom: "20px" }}>
          Take quizzes and exams based on your generated curriculum. 
          <strong style={{ color: "#FFD700" }}> Complete all lessons to unlock Chapter Exams!</strong>
        </p>

        {error && (
          <div className="alert alert-error" data-testid="exam-error" style={{
            background: "#4d1a1a",
            border: "1px solid #ff6b6b",
            color: "#ff6b6b",
            padding: "15px",
            borderRadius: "8px",
            marginBottom: "20px"
          }}>
            {error}
          </div>
        )}

        {/* Grade Level Selection */}
        {!gradeLevel && (
          <div>
            <h4 style={{ color: "#FFD700", marginBottom: "15px" }}>
              Select Your Grade Level:
            </h4>
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: "10px"
            }}>
              {gradeLevels.map((grade) => (
                <button
                  key={grade.id}
                  onClick={() => setGradeLevel(grade.id)}
                  data-testid={`exam-grade-${grade.id}`}
                  className="grade-level-btn"
                  style={{
                    padding: "15px",
                    background: "#222",
                    border: "2px solid #FFD700",
                    marginTop: 0,
                    color: "#FFD700"
                  }}
                >
                  {grade.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Subject Selection for Exams */}
        {gradeLevel && (
          <div>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              marginBottom: "20px"
            }}>
              <h4 style={{ color: "#FFD700", margin: 0 }}>
                Available Exams for {gradeLevel}:
              </h4>
              <button
                onClick={() => setGradeLevel("")}
                style={{ width: "auto", padding: "8px 15px", marginTop: 0 }}
              >
                Change Grade
              </button>
            </div>

            {getAvailableSubjects().length === 0 ? (
              <div className="card" style={{ 
                background: "#4d1a1a", 
                border: "1px solid #ff6b6b",
                textAlign: "center"
              }}>
                <div style={{ fontSize: "3rem", marginBottom: "15px" }}>📚</div>
                <h4 style={{ color: "#ff6b6b" }}>No Curriculum Available</h4>
                <p style={{ color: "#aaa", marginBottom: "15px" }}>
                  You need to generate curriculum before you can take exams.
                </p>
                <p style={{ color: "#FFD700", fontSize: "0.9rem" }}>
                  Go to <strong>Library</strong> → Select a subject → Click <strong>"Generate Curriculum"</strong>
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                {getAvailableSubjects().map((subject) => {
                  const progress = lessonProgress[subject.id];
                  const canTakeExam = progress?.can_take_exam;
                  
                  return (
                    <div
                      key={subject.id}
                      style={{
                        background: "#222",
                        padding: "20px",
                        borderRadius: "8px",
                        borderLeft: `4px solid ${canTakeExam ? '#90EE90' : '#FFD700'}`
                      }}
                    >
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: "15px"
                      }}>
                        <div>
                          <h4 style={{ color: "#FFD700", margin: "0 0 5px 0" }}>
                            {subject.icon} {subject.name}
                          </h4>
                          <p style={{ margin: 0, color: "#aaa", fontSize: "0.9rem" }}>
                            {subject.description?.slice(0, 100)}...
                          </p>
                          <div style={{ 
                            marginTop: "10px",
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap"
                          }}>
                            <span style={{
                              background: "#1a4d1a",
                              color: "#90EE90",
                              padding: "3px 8px",
                              borderRadius: "4px",
                              fontSize: "0.8rem"
                            }}>
                              ✓ Curriculum Ready
                            </span>
                            {renderProgressIndicator(subject.id)}
                          </div>
                        </div>
                        
                        <div style={{ 
                          display: "flex", 
                          flexDirection: "column", 
                          gap: "10px",
                          minWidth: "150px"
                        }}>
                          <button
                            onClick={() => handleStartExam(subject.id, "quiz")}
                            disabled={loading}
                            data-testid={`take-quiz-${subject.id}`}
                            style={{
                              marginTop: 0,
                              background: "#1a4d1a",
                              fontSize: "0.9rem"
                            }}
                          >
                            📝 Take Lesson Quiz
                          </button>
                          <button
                            onClick={() => handleStartExam(subject.id, "chapter")}
                            disabled={loading || !canTakeExam}
                            data-testid={`take-exam-${subject.id}`}
                            style={{
                              marginTop: 0,
                              background: canTakeExam ? "#87CEEB" : "#444",
                              color: canTakeExam ? "#000" : "#888",
                              fontSize: "0.9rem",
                              cursor: canTakeExam ? "pointer" : "not-allowed",
                              opacity: canTakeExam ? 1 : 0.7
                            }}
                            title={canTakeExam ? "Take the chapter exam" : "Complete all lessons first to unlock"}
                          >
                            {canTakeExam ? "📋 Take Chapter Exam" : "🔒 Exam Locked"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="card" style={{ 
        background: "#1a2a1a", 
        borderLeft: "4px solid #90EE90",
        marginTop: "20px"
      }}>
        <h4 style={{ color: "#90EE90", margin: "0 0 10px 0" }}>
          📖 How Exams Work
        </h4>
        <ul style={{ margin: 0, paddingLeft: "20px", color: "#ccc", fontSize: "0.9rem" }}>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#FFD700" }}>Lesson Quizzes:</strong> Available anytime to test your knowledge on individual lessons
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#FFD700" }}>Chapter Exams:</strong> 🔒 <span style={{ color: "#ff6b6b" }}>Locked</span> until you complete ALL lessons in the Library
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#FFD700" }}>To Unlock Exams:</strong> Go to Library → Click through each lesson to mark it complete
          </li>
          <li style={{ background: "transparent", border: "none", padding: "0" }}>
            <strong style={{ color: "#FFD700" }}>Passing Score:</strong> 70% or higher to pass exams and quizzes
          </li>
        </ul>
      </div>
    </div>
  );
}

export default ExamSection;
